// URL parameters. Pure: parseParams takes a query string so it can be unit tested.
import { parseJoutsenoTime } from '../world/environment/almanac';

export type QualityTier = 'low' | 'medium' | 'high';

export interface AppParams {
  source: 'live' | 'replay' | null; // null = live first, replay fallback
  replay: string; // file name in public/replays/
  seek: number; // seconds into the replay
  speed: number; // 0.25 .. 8
  quality: QualityTier | null; // null = run the benchmark
  autostart: boolean;
  debug: boolean;
  bench: boolean;
  /** Fixed-step capture: race time advances 1/60 s per rendered frame, for screenshots. */
  capture: boolean;
  /** Fixed random seed for everything that is not already deterministic (sound variation). */
  seed: number | null;
  /** Jump the recorded race straight to a moment worth looking at. */
  moment: Moment | null;
  /** Start the stadium's clock at this instant (ms) instead of now: `?time=2026-12-24T18:00` (Joutseno time). */
  time: number | null;
}

export const MOMENTS = ['gates', 'ham', 'catnip', 'podium'] as const;
export type Moment = typeof MOMENTS[number];

export const DEFAULT_REPLAY = 'catderby-2026-10-05.tsv';

function flag(v: string | null): boolean {
  return v !== null && v !== '0' && v !== 'false';
}

function num(v: string | null, fallback: number, min: number, max: number): number {
  if (v === null || v.trim() === '') return fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function parseParams(search: string): AppParams {
  const q = new URLSearchParams(search);
  const source = q.get('source');
  const quality = q.get('quality');
  const replay = q.get('replay');
  return {
    source: source === 'live' || source === 'replay' ? source : null,
    // Only a bare file name: no paths out of public/replays/.
    replay: replay && /^[\w.-]+$/.test(replay) ? replay : DEFAULT_REPLAY,
    seek: num(q.get('seek'), 0, 0, Number.MAX_SAFE_INTEGER),
    speed: num(q.get('speed'), 1, 0.25, 8),
    quality: quality === 'low' || quality === 'medium' || quality === 'high' ? quality : null,
    autostart: flag(q.get('autostart')),
    debug: flag(q.get('debug')),
    bench: flag(q.get('bench')),
    capture: flag(q.get('capture')),
    seed: ((v) => (v !== null && /^\d+$/.test(v) ? Number(v) >>> 0 : null))(q.get('seed')),
    moment: ((v) => (MOMENTS as readonly string[]).includes(v ?? '') ? v as Moment : null)(q.get('moment')),
    time: parseJoutsenoTime(q.get('time')),
  };
}

let cached: AppParams | null = null;
/** Parameters of the current page, parsed once. */
export function params(): AppParams {
  if (!cached) cached = parseParams(typeof location === 'undefined' ? '' : location.search);
  return cached;
}
